/**
 * Invitations Tab — admin surface for inviting new team members.
 *
 * All reads/writes talk to `invitationsAPI`, which is tenant-scoped on
 * the backend (`routes/invitations.py` passes through `TenantDB` for
 * admin endpoints). Nothing on this screen can show another org's
 * invitations, by construction.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { invitationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Mail, Send, XCircle, RefreshCw, Copy, Check } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'medic', label: 'Medic' },
  { value: 'ops_coordinator', label: 'Ops Coordinator' },
  { value: 'team_leader', label: 'Team Leader' },
  { value: 'manager', label: 'Manager' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  accepted: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  revoked: 'bg-red-500/15 text-red-400 border-red-500/20',
  expired: 'bg-white/10 text-[#A1A1AA] border-white/10',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function InvitationsTab() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeAll, setIncludeAll] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('medic');
  const [submitting, setSubmitting] = useState(false);
  const [lastAcceptUrl, setLastAcceptUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchInvites = useCallback(async () => {
    try {
      setLoading(true);
      const res = await invitationsAPI.list(includeAll);
      setInvites(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [includeAll]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter an email'); return; }
    setSubmitting(true);
    try {
      const res = await invitationsAPI.create({ email: email.trim(), role });
      toast.success(`Invitation sent to ${email}`);
      setEmail('');
      setRole('medic');
      setShowInvite(false);
      setLastAcceptUrl(res.data?.accept_url || null);
      await fetchInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this invitation? The link will stop working immediately.')) return;
    try {
      await invitationsAPI.revoke(id);
      toast.success('Invitation revoked');
      await fetchInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke');
    }
  };

  const handleResend = async (id) => {
    try {
      const res = await invitationsAPI.resend(id);
      toast.success('Invitation resent');
      setLastAcceptUrl(res.data?.accept_url || null);
      await fetchInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resend');
    }
  };

  const copyAcceptUrl = async () => {
    if (!lastAcceptUrl) return;
    try {
      await navigator.clipboard.writeText(lastAcceptUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="space-y-4" data-testid="invitations-tab">
      {lastAcceptUrl && (
        <Card className="bg-[#1ABC9C]/5 border-[#1ABC9C]/30">
          <CardContent className="pt-6 flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#1ABC9C] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#F2F2F2]">
                Last invitation link (copy if email didn't arrive)
              </p>
              <p className="text-xs text-[#A1A1AA] truncate" data-testid="last-accept-url">
                {lastAcceptUrl}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-[#1ABC9C]/30 text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
              onClick={copyAcceptUrl}
              data-testid="copy-accept-url-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-[#F2F2F2]">Invitations</CardTitle>
            <CardDescription className="text-[#C7C7C7]">
              Invite colleagues to join your workspace. They set their own
              password; you choose their role.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              className="border-white/10 text-[#C7C7C7]"
              onClick={() => setIncludeAll((v) => !v)}
              data-testid="toggle-all-invites-btn"
            >
              {includeAll ? 'Show pending only' : 'Show all history'}
            </Button>
            <Button
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              onClick={() => setShowInvite(true)}
              data-testid="invite-user-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Invite teammate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#A1A1AA]">Email</TableHead>
                <TableHead className="text-[#A1A1AA]">Role</TableHead>
                <TableHead className="text-[#A1A1AA]">Invited by</TableHead>
                <TableHead className="text-[#A1A1AA]">Status</TableHead>
                <TableHead className="text-[#A1A1AA]">Expires</TableHead>
                <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#A1A1AA] py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : invites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#A1A1AA] py-8" data-testid="no-invites-empty">
                    No invitations yet. Click "Invite teammate" to send your first one.
                  </TableCell>
                </TableRow>
              ) : (
                invites.map((inv) => (
                  <TableRow key={inv.id} className="border-white/5 hover:bg-white/5" data-testid={`invite-row-${inv.id}`}>
                    <TableCell className="text-[#F2F2F2] font-medium">{inv.email}</TableCell>
                    <TableCell className="text-[#C7C7C7] capitalize">{inv.role.replace('_', ' ')}</TableCell>
                    <TableCell className="text-[#A1A1AA]">{inv.invited_by_name || '—'}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[inv.status] || 'bg-white/10 text-[#C7C7C7]'}>
                        {inv.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#A1A1AA]">{formatDate(inv.expires_at)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      {inv.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
                            onClick={() => handleResend(inv.id)}
                            data-testid={`resend-invite-${inv.id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Resend
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:bg-red-500/10"
                            onClick={() => handleRevoke(inv.id)}
                            data-testid={`revoke-invite-${inv.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Revoke
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-[#2B2B2B] border-white/10" aria-describedby="invite-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-[#F2F2F2]">Invite a teammate</DialogTitle>
            <DialogDescription id="invite-dialog-desc" className="text-[#C7C7C7]">
              We'll email them a link to set their password and join your workspace.
              The link expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email" className="text-[#F2F2F2]">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@yourorg.co.uk"
                className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2] placeholder:text-[#C7C7C7]/50"
                required
                data-testid="invite-dialog-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#F2F2F2]">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#3D3D3D] text-[#F2F2F2]" data-testid="invite-dialog-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-[#F2F2F2]">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#A1A1AA]">
                Admin role cannot be issued by invitation — promote an existing user
                in the User Management tab after they join.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowInvite(false)}
                className="text-[#C7C7C7]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                disabled={submitting}
                data-testid="invite-dialog-submit-btn"
              >
                {submitting ? 'Sending…' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
